/**
 * Reconciles deal-A and deal-C gift line items with their parent line items.
 * If a gift item carries `_gift_for: <session>` but no parent item carries
 * the matching `_deal_session: <session>`, the gift is removed. When a parent
 * exists, the gift line's quantity is kept equal to the parent's (1+1 per
 * unit — bumping the product with + in the drawer bumps the gift too).
 *
 * When the Deal C promo is over (see `window.dealCPromoActive`, rendered from
 * the `custom.deal_c_active` / `custom.deal_c_ends_at` shop metafields — the
 * same switch the discount function reads), its gift lines are removed from
 * carts that were filled while it ran. Without this the customer reaches
 * checkout with a "gift" the function no longer discounts and quietly pays for
 * it. Deal A gifts are €0 SKUs and are never touched by the switch.
 *
 * Mirrors the busy/debounce/checkout-block pattern from free-gift.js so that
 * the user can't slip past while reconciliation is mid-flight.
 *
 * Independent of the legacy free-gift.js (different busy class, different
 * line-item identifiers — they will not interfere with each other).
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 150;
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)\.js(\?|$)/;
  const BUSY_CLASS = 'deal-gift-reconcile-busy';
  const CHECKOUT_SELECTOR = '[name="checkout"]';

  let inFlight = false;
  let debounceTimer = null;

  // Inject CSS to visually disable checkout buttons while we reconcile.
  const style = document.createElement('style');
  style.textContent =
    'body.' +
    BUSY_CLASS +
    ' ' +
    CHECKOUT_SELECTOR +
    ' { pointer-events: none !important; opacity: 0.5 !important; cursor: wait !important; }';
  (document.head || document.documentElement).appendChild(style);

  function setBusy(busy) {
    if (!document.body) return;
    document.body.classList.toggle(BUSY_CLASS, busy);
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((btn) => {
      btn.disabled = busy;
      if (busy) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
  }

  function scheduleReconcile(fromMutation) {
    if (fromMutation) setBusy(true);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runReconcile, DEBOUNCE_MS);
  }

  async function runReconcile() {
    debounceTimer = null;
    await reconcile();
    if (debounceTimer === null && !inFlight) setBusy(false);
  }

  async function flushReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    while (inFlight) {
      await new Promise((r) => setTimeout(r, 20));
    }
    await reconcile();
  }

  async function reconcile() {
    if (inFlight) return;
    inFlight = true;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();

      const parentsBySession = new Map();
      const dealCPromoOver = window.dealCPromoActive === false;

      for (const item of cart.items) {
        const props = item.properties || {};
        // Deals A and C both link a gift line to a parent via _deal_session.
        if ((props._deal === 'a' || props._deal === 'c') && props._deal_session) {
          parentsBySession.set(props._deal_session, item);
        }
      }

      const orphans = [];
      const outOfSync = [];
      for (const item of cart.items) {
        const props = item.properties || {};
        if (!props._gift_for) continue;
        const parent = parentsBySession.get(props._gift_for);
        if (!parent) {
          orphans.push(item);
        } else if (dealCPromoOver && (parent.properties || {})._deal === 'c') {
          // Promo is over: drop the gift, keep the paid product. Without this
          // the customer checks out with a gift the function no longer
          // discounts and pays for it without noticing.
          orphans.push(item);
        } else if (item.quantity !== parent.quantity) {
          // 1+1 per unit: the gift line mirrors the parent's quantity, both
          // when the customer bumps the parent with +/- and when they try to
          // inflate the gift itself. (The Discount Function refuses gift
          // quantities above the parent's, so an un-synced bump also loses
          // the discount until this correction lands.)
          outOfSync.push({ key: item.key, quantity: parent.quantity });
        }
      }

      if (orphans.length === 0 && outOfSync.length === 0) return;

      // Mutate sequentially to avoid race conditions inside Shopify's cart endpoint.
      let mutated = false;
      for (const orphan of orphans) {
        const ok = await changeQty(orphan.key, 0);
        if (ok) mutated = true;
      }
      for (const sync of outOfSync) {
        // May fail on insufficient stock for an increase — swallowed, the
        // discount then simply applies to the smaller gift quantity.
        const ok = await changeQty(sync.key, sync.quantity);
        if (ok) mutated = true;
      }

      if (mutated) {
        // Refresh the cart drawer markup.
        //
        // The drawer (theme.js `register("quick-cart", ...)`) listens to
        // `apps:product-added-to-cart` on document and calls its internal
        // `refreshQuickCart()`, which GETs `?section_id=quick-cart` and
        // swaps the rendered items in place. Misleading name — there is
        // no "add" semantics tied to it; the cart-count updater is the
        // only other listener and it just re-counts the cart. Safe to
        // fire on removal.
        //
        // Race condition we have to defeat: when the parent line is
        // removed via the cart drawer, the theme fires its own refresh
        // first. `refreshQuickCart()` sets `_refreshInProgress = true`
        // and drops re-entries until the in-flight GET resolves. By the
        // time our reconcile finishes removing the gift (~250–400ms),
        // the theme's refresh may still be mid-flight, so a single
        // dispatch right after our removal can be swallowed — the
        // drawer ends up showing the now-stale gift line until another
        // user action. Fan the dispatch across three timepoints so at
        // least one fires after `_refreshInProgress` clears.
        //
        // `cart:updated` only fires ONCE — our own document listener on
        // it (line ~140) re-enters reconcile, so dispatching it 3×
        // would queue 3 redundant /cart.js round-trips with busy-flashes
        // for nothing.
        const refreshDrawer = () => {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: {} }));
        };
        refreshDrawer();
        setTimeout(refreshDrawer, 500);
        setTimeout(refreshDrawer, 1100);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (e) {
      // Swallow — this is a self-healing background task.
    } finally {
      inFlight = false;
    }
  }

  async function changeQty(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  /* ---------------- triggers ---------------- */

  // 1) Initial page load — passive (no busy state, cart assumed stable).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleReconcile(false), { once: true });
  } else {
    scheduleReconcile(false);
  }

  // 2) Theme's cart:updated event — actual mutation, block checkout.
  document.addEventListener('cart:updated', () => scheduleReconcile(true));

  // 3) Safety net — flush before checkout if user beats the busy block.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (form.dataset._dealReconcileFlushed === '1') {
        delete form.dataset._dealReconcileFlushed;
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      setBusy(true);
      flushReconcile().finally(() => {
        setBusy(false);
        form.dataset._dealReconcileFlushed = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter);
        } else {
          form.submit();
        }
      });
    },
    true,
  );

  // 4) Intercept any successful cart mutation fetch (covers third-party / theme code paths).
  const originalFetch = window.fetch;
  window.fetch = function () {
    const args = arguments;
    const promise = originalFetch.apply(this, args);
    if (!inFlight) {
      const url = args[0] instanceof Request ? args[0].url : String(args[0] || '');
      if (CART_MUTATION_RE.test(url)) {
        setBusy(true);
        promise
          .then((res) => {
            if (res && res.ok) scheduleReconcile(true);
            else if (debounceTimer === null && !inFlight) setBusy(false);
          })
          .catch(() => {
            if (debounceTimer === null && !inFlight) setBusy(false);
          });
      }
    }
    return promise;
  };
})();
